import { getLegalEntities } from '@dealstack/frontend/app/services/Person.service.ts';
import { updateDeal } from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { PersonType } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';

interface Props {
	dealId?: string;
	onUpdated?: () => void;
}

/**
 * CAP-1974: drives the deal-level "Nominee Structure" card in Deal Settings.
 *
 * `Deal.hasNomineeStructure` is the authoritative master gate — `DealRepository.getDealNomineeFlags`
 * reads that stored column alone — so this hook only mutates it (via PATCH deal) and reports how many
 * legal persons are currently marked as nominees, which the card uses to spell out what switching the
 * gate off hides. Per-entity eligibility is set by the "Nominee structures" toggle on that entity's
 * Securities tab.
 */
export const useNomineeStructureCard = ({ dealId, onUpdated }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const { hasPermission: canManage } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.EDITOR,
	});

	const legalPersonsRequest = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId }],
		queryFn: () =>
			getLegalEntities(API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, {
				dealId: dealId ?? '',
			}),
		enabled: !!dealId,
	});

	const nomineePersonCount = useMemo(
		() =>
			(legalPersonsRequest.data ?? []).filter(
				(person) => person.type === PersonType.LEGAL && person.hasNomineeStructure
			).length,
		[legalPersonsRequest.data]
	);

	const { mutate, isPending } = useMutation({
		mutationFn: (variables: Parameters<typeof updateDeal>[0]) => updateDeal(variables),
	});

	const setHasNomineeStructure = (next: boolean) => {
		if (!dealId) {
			return;
		}
		mutate(
			{ dealId, hasNomineeStructure: next },
			{
				onSuccess: () => {
					onUpdated?.();
					showToastV2({
						title: tAdminDashboard('deal_settings.nominee_structure.updated', {
							defaultValue: 'Nominee structure updated',
						}),
					});
				},
				onError: () => {
					showToastV2({
						state: 'error',
						title: tAdminDashboard('deal_settings.nominee_structure.update_failed', {
							defaultValue: 'Could not update nominee structure',
						}),
					});
				},
			}
		);
	};

	return {
		nomineePersonCount,
		setHasNomineeStructure,
		canManage,
		isPending,
	};
};
